/**
 * Products Resource Client
 * Axios-based API client for product endpoints
 */

// Import types from utils to avoid duplication
import type { Product, ProductVariant, ProductPrice } from '../utils/products';

import { ApiClient } from './apiClient';

export interface GetProductsOptions {
    storeId: string;
    productIds?: string[];
    variantIds?: string[];
    includeVariants?: boolean;
    includePrices?: boolean;
}

// Re-export types for convenience
export type { Product, ProductVariant, ProductPrice };

export class ProductsResource {
    constructor(private apiClient: ApiClient) { }

    /**
     * Get all products for a store
     */
    async getProducts(options: GetProductsOptions): Promise<Product[]> {
        const { storeId, variantIds, includeVariants = true, includePrices = true } = options;

        const response = await this.apiClient.post<Product[]>(
            '/api/v1/products',
            {
                storeId,
                variantIds,
                includeVariants,
                includePrices,
            }
        );

        return Array.isArray(response) ? response : (response as any).items ?? [];
    }

    /**
     * Get a specific product by ID
     */
    async getProduct(productId: string, options: GetProductsOptions): Promise<Product | null> {
        const { storeId } = options;

        try {
            const response = await this.apiClient.post<Product>(
                `/api/v1/products/${productId}`,
                {
                    productId,
                    storeId,
                }
            );

            return response || null;
        } catch (error) {
            console.error('Error fetching product:', error);
            return null;
        }
    }

    /**
     * Get multiple products by IDs
     */
    async getProductsByIds(productIds: string[], options: GetProductsOptions): Promise<Product[]> {
        const { storeId, includeVariants = true, includePrices = true } = options;

        const fetchPromises = productIds.map(async (productId: string) => {
            return this.getProduct(productId, { storeId, includeVariants, includePrices });
        });

        const results = await Promise.all(fetchPromises);
        return results.filter((product): product is Product => product !== null);
    }

    /**
     * Get products by variant IDs (efficient filtering on the backend)
     */
    async getProductsByVariantIds(variantIds: string[], options: GetProductsOptions): Promise<Product[]> {
        const { storeId, includeVariants = true, includePrices = true } = options;

        const response = await this.apiClient.post<Product[]>(
            '/api/v1/products',
            {
                storeId,
                variantIds, // Backend will filter products that have these variants
                includeVariants,
                includePrices,
            }
        );

        return Array.isArray(response) ? response : (response as any).items ?? [];
    }
}
